import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/rootReducer';
import { PrefilledLineData } from '../../../types/prefilled-line/PrefilledLineData';
import {
  PrefilledLinesErrorType,
  usePrefilledLinesErrorMessage,
  usePrefilledLinesErrorType,
  usePrefilledLinesLoading,
  usePrefilledLinesSaving,
} from '../../../redux/prefilled-lines/PrefilledLinesHooks';
import { PrefilledLinesList } from '../../../components/prefilled-lines/PrefilledLinesList';
import { Box, Button, CircularProgress } from '@mui/material';
import {
  useAppSelector,
  useEpochDispatch,
} from '../../../redux/hooks/ReduxHooks';
import { GoogleApiTools } from '../../../tools/GoogleApiTools';
import { DropFileModal } from '../../../components/modals/drop-file-modal/DropFileModal';
import PrefilledLineImportFileSchema from '../../../generated/json-schema/PrefilledLineImportFile.json';
import { PrefilledLinesSlice } from '../../../redux/prefilled-lines/PrefilledLinesSlice';
import { usePrefilledLinesAutoSave } from '../../../hooks/PrefilledLinesAutoSaveHooks';
import { PrefilledLinesToolbar } from '../../../components/toolbar/PrefilledLinesToolbar';
import FileSaver from 'file-saver';
import { PrefilledLineImportFile } from '../../../types/prefilled-line/PrefilledLineImportFile';
import { useSelectedItemHook } from '../../../hooks/SelectedItemHook';
import { PrefilledLineEditorForm } from '../../../components/prefilled-lines/editor-form/PrefilledLineEditorForm';
import { PrefilledLineEditorModal } from '../../../components/prefilled-lines/editor-form/PrefilledLineEditorModal';
import Dinero from 'dinero.js';
import { SimpleModal } from '../../../components/modals/SimpleModal';

const containerSx = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 1,
} as const;

const centeredTextSx = {
  textAlign: 'center',
  marginBottom: 1,
} as const;

const inlineButtonsSx = {
  margin: (theme: any) => theme.spacing(0.5, 0),
} as const;

export const PrefilledLinesNavigator = () => {
  const epochDispatch = useEpochDispatch();

  // Enable autosave.
  usePrefilledLinesAutoSave(true);

  const [localLoading, setLocalLoading] = useState(false);
  const loading = usePrefilledLinesLoading() || localLoading;
  const saving = usePrefilledLinesSaving();

  const error = usePrefilledLinesErrorType();
  const errorMessage = usePrefilledLinesErrorMessage();
  const configFileId = useAppSelector(
    (state) => state.prefilledLines.configFileId,
  );

  const [lineToDeleteIndex, setLineToDeleteIndex] = useState<number | null>(
    null,
  );

  const prefilledLines = useSelector<RootState, PrefilledLineData[]>((state) =>
    state.prefilledLines.linesList
      .map((uuid) => state.prefilledLines.linesByUuid[uuid])
      .filter((line) => line != null),
  );
  const selection = useSelectedItemHook(prefilledLines);

  const handleRemoveSelected = useCallback(() => {
    if (selection.selectedIndex != null) {
      setLineToDeleteIndex(selection.selectedIndex);
    }
  }, [selection.selectedIndex]);

  const confirmRemoveSelected = useCallback(() => {
    if (lineToDeleteIndex != null) {
      epochDispatch(PrefilledLinesSlice.actions.removeLine, {
        index: lineToDeleteIndex,
      });
      setLineToDeleteIndex(null);
    }
  }, [epochDispatch, lineToDeleteIndex]);

  const handleReset = useCallback(() => {
    epochDispatch(PrefilledLinesSlice.actions.resetFile, {});
  }, [epochDispatch]);

  const handleExport = useCallback(async () => {
    setLocalLoading(true);
    if (configFileId != null) {
      try {
        const result = await GoogleApiTools.openFile(configFileId);
        const fileContentBlob = new Blob([result.content], {
          type: 'application/json;charset=utf-8',
        });
        FileSaver.saveAs(fileContentBlob, 'export-ligne-preremplies.json');
      } catch (e: any) {
        alert("Impossible d'exporter le fichier : " + e.message);
      }
    } else {
      alert(
        "Impossible d'exporter le fichier : aucun fichier de config existant",
      );
    }
    setLocalLoading(false);
  }, [configFileId]);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const handleImport = useCallback(() => {
    setImportModalOpen(true);
  }, []);
  const handleCloseImport = useCallback(() => {
    setImportModalOpen(false);
  }, []);
  const handleSubmitImport = useCallback(
    (content: string) => {
      handleCloseImport();
      const importContent: PrefilledLineImportFile = JSON.parse(content); // Import has been validated by the modal, no need to try catch.
      epochDispatch(PrefilledLinesSlice.actions.importLinesConfig, {
        importContent,
      });
    },
    [epochDispatch, handleCloseImport],
  );

  // string if a prefilled line is edited, boolean (true) if a new line is edited or null if the modal is closed.
  const [editPrefilledLineId, setEditPrefilledLineId] = useState<
    string | true | null
  >(null);
  const handleFormSubmit = useCallback(
    (data: PrefilledLineEditorForm.FormValues) => {
      if (editPrefilledLineId === true) {
        // We create a new line at the selection's index.
        epochDispatch(PrefilledLinesSlice.actions.addLine, {
          index: (selection.selectedIndex || prefilledLines.length - 1) + 1,
          formValues: data,
        });
      } else if (editPrefilledLineId != null) {
        // We update the line.
        epochDispatch(PrefilledLinesSlice.actions.updateLine, {
          uuid: editPrefilledLineId,
          formValues: data,
        });
      }
      setEditPrefilledLineId(null);
    },
    [
      editPrefilledLineId,
      epochDispatch,
      prefilledLines.length,
      selection.selectedIndex,
    ],
  );
  const editorForm = PrefilledLineEditorForm.useForm(
    {
      name: '',
      checked: false,
      type: '',
      description: '',
      amount: '',
      color: '#ffffff',
      category: undefined,
    },
    handleFormSubmit,
  );
  const handleEditPrefilledLine = useCallback(
    (lineId: string | null) => {
      if (lineId != null) {
        const prefilledLineToEdit = prefilledLines.find(
          (l) => l.uuid === lineId,
        );
        if (!prefilledLineToEdit) {
          return;
        }
        setEditPrefilledLineId(lineId);
        editorForm.resetValues({
          name: prefilledLineToEdit.name,
          checked: prefilledLineToEdit.checked,
          type: prefilledLineToEdit.type,
          description: prefilledLineToEdit.description,
          amount:
            prefilledLineToEdit.amount &&
            prefilledLineToEdit.amount.amount !== 0
              ? '' + Dinero(prefilledLineToEdit.amount).toUnit()
              : '',
          color: prefilledLineToEdit.color ?? '#ffffff',
          category: prefilledLineToEdit.category,
        });
      } else {
        setEditPrefilledLineId(true);
        editorForm.resetValues({
          name: '',
          checked: false,
          type: '',
          description: '',
          amount: '',
          color: '#ffffff',
          category: undefined,
        });
      }
    },
    [editorForm, prefilledLines],
  );

  if (loading) {
    return (
      <Box sx={containerSx}>
        <CircularProgress color={'secondary'} />
      </Box>
    );
  } else {
    if (error !== PrefilledLinesErrorType.NO_ERRORS) {
      return (
        <Box sx={containerSx}>
          <Box component="span" sx={centeredTextSx}>
            Une erreur s&apos;est produite pendant le chargement des lignes
            pré-remplies enregistrées.
          </Box>
          {error === PrefilledLinesErrorType.INVALID_EXISTING_FILE ? (
            <Box component="span" sx={centeredTextSx}>
              Les lignes pré-remplies enregistrées sont corrompues. Nous vous
              invitons à{' '}
              <Button
                sx={inlineButtonsSx}
                variant={'outlined'}
                size={'small'}
                onClick={handleExport}
              >
                Exporter
              </Button>{' '}
              leur configuration puis les{' '}
              <Button
                sx={inlineButtonsSx}
                variant={'outlined'}
                size={'small'}
                onClick={handleReset}
              >
                Réinitialiser
              </Button>{' '}
              pour tenter de corriger le problème.
            </Box>
          ) : null}
          {errorMessage != null ? (
            <Box component="span" sx={centeredTextSx}>
              {errorMessage}
            </Box>
          ) : null}
        </Box>
      );
    } else {
      return (
        <>
          <PrefilledLineEditorModal
            open={editPrefilledLineId != null}
            onClose={() => {
              setEditPrefilledLineId(null);
            }}
            form={editorForm}
          />
          <SimpleModal
            title={'Confirmation de suppression'}
            buttons={['Oui', 'Non']}
            onButtonClicked={(buttonIndex) => {
              if (buttonIndex === 0) {
                confirmRemoveSelected();
              } else {
                setLineToDeleteIndex(null);
              }
            }}
            open={lineToDeleteIndex != null}
          >
            Êtes-vous sûr(e) de vouloir supprimer cette ligne pré-remplie ?
          </SimpleModal>
          <DropFileModal
            title={'Import de lignes pré-remplies'}
            message={'Importe des lignes pré-remplies depuis un fichier JSON.'}
            submitButtonLabel={'Importer'}
            jsonSchema={PrefilledLineImportFileSchema}
            onSubmitClicked={handleSubmitImport}
            onCancelClicked={handleCloseImport}
            open={importModalOpen}
            onClose={handleCloseImport}
          />
          <Box
            sx={{
              flex: 1,
              flexDirection: 'column',
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <Box sx={{ margin: 1 }}>
              <PrefilledLinesToolbar
                onAddClicked={() => {
                  handleEditPrefilledLine(null);
                }}
                onEditClicked={() => {
                  handleEditPrefilledLine(selection.selectedUuid);
                }}
                editDisabled={selection.selectedIndex == null}
                onDeleteClicked={handleRemoveSelected}
                deleteDisabled={selection.selectedIndex == null}
                onImportClicked={handleImport}
                onExportClicked={handleExport}
                saving={saving}
              />
            </Box>
            <Box style={{ flex: 1 }}>
              <PrefilledLinesList
                lines={prefilledLines}
                selectedIndex={selection.selectedIndex}
                setSelectedIndex={selection.setSelectedIndex}
              />
            </Box>
          </Box>
        </>
      );
    }
  }
};
